import { CodedProperty, NamedProperty, ResolutionProperty } from "sprint-common-web";

import { GetOnPremOrderDetailsResponseItem } from "../../../common/api/orders-api/on-prem-order/GetOnPremOrderDetailsResponse";
import { OnPremOrderSource } from "../../../common/api/orders-api/on-prem-order/GetOnPremOrderListResponse";
import {
    resolveOrderContentTitle,
    resolveOrderContentVersion
} from "../../../common/api/orders-api/order/ContentReference";
import { NoteItem } from "../../../common/components/notes/hooks/state/NoteItem";
import { DynamicSubtitleType } from "../../../common/types/DynamicSubtitleType";
import { OrderContentReference } from "../../../common/types/OrderContentReference";
import { OnPremOrderStatus, mapToOnPremOrderStatus } from "../../common/types/OnPremOrderStatus";
import { OrderAssetAvailability } from "../../common/types/OrderAssetAvailability";
import { OrderResolutionDimensions } from "../../common/types/OrderResolutionDimensions";

export interface OnPremOrderDashboardItem {
    id: string;
    pONumber: string;
    contentTitle?: string;
    content: OrderContentReference;
    airline: CodedProperty;
    system: NamedProperty;
    dueDate: string;
    startDate: string;
    modifiedBy?: string;
    targetResolution?: OrderResolutionDimensions;
    onPremAssets: OnPremAssetsDashboardItem;
    status: OnPremOrderStatus;
    materialSpecification: MaterialSpecification;
    deliveryTo?: string;
    deliveryDetails?: string;
    source: OnPremOrderSource;
    isTrailer?: boolean;
    notes?: string;
    assetNotes: NoteItem[];
}

interface OnPremAssetsDashboardItem {
    video: OnPremOrderVideoDashboardItem;
    soundtracks: OnPremOrderSoundtrackDashboardItem[];
    subtitles: OnPremOrderSubtitleDashboardItem[];
}

interface OnPremOrderVideoDashboardItem {
    assetId?: string;
    filePath?: string;
    targetFilename: string;
    aspectRatio: NamedProperty;
    subtitle: CodedProperty;
    availability: OrderAssetAvailability;
    targetResolution?: ResolutionProperty;
    sourceResolution?: ResolutionProperty;
    nonMatchingVideos: OnPremOrderNonMatchingVideoDashboardItem[];
}

interface OnPremOrderNonMatchingVideoDashboardItem {
    assetId: string;
    subtitle: CodedProperty;
    aspectRatio: NamedProperty;
    resolution: ResolutionProperty;
}

interface OnPremOrderSoundtrackDashboardItem {
    assetId?: string;
    filePath?: string;
    language: CodedProperty;
    availability: OrderAssetAvailability;
}

interface OnPremOrderSubtitleDashboardItem {
    assetId?: string;
    filePath?: string;
    language: CodedProperty;
    availability: OrderAssetAvailability;
    subtitleType: DynamicSubtitleType | undefined;
    targetFilename?: string;
}

interface MaterialSpecification {
    encodingFormat?: string;
    aspectRatio?: string;
    bitRate?: string;
    resolution?: string;
    runtime?: string;
}

export const mapToOnPremOrderDashboardItem = (order: GetOnPremOrderDetailsResponseItem): OnPremOrderDashboardItem => {
    return {
        id: order.id,
        contentTitle: order.content
            ? `${resolveOrderContentTitle(order.content)} - ${resolveOrderContentVersion(order.content)}`
            : undefined,
        content: order.content,
        airline: order.airline,
        system: order.system,
        dueDate: new Date(order.dueDate).toString(),
        startDate: new Date(order.startDate).toString(),
        targetResolution: order.targetResolution,
        onPremAssets: {
            video: {
                assetId: order.onPremAssets.video.assetId,
                targetFilename: order.onPremAssets.video.targetFilename,
                filePath: order.onPremAssets.video.filePath,
                aspectRatio: order.onPremAssets.video.aspectRatio,
                availability: order.onPremAssets.video.availability,
                sourceResolution: order.onPremAssets.video.sourceResolution,
                targetResolution: order.onPremAssets.video.targetResolution,
                subtitle: order.onPremAssets.video.subtitle,
                nonMatchingVideos: order.onPremAssets.video.nonMatchingVideos.map((nmv) => ({
                    assetId: nmv.assetId,
                    subtitle: nmv.subtitle,
                    aspectRatio: nmv.aspectRatio,
                    resolution: nmv.resolution
                }))
            },
            soundtracks: order.onPremAssets.soundtracks.map((st) => ({
                assetId: st.assetId,
                filePath: st.filePath,
                language: st.language,
                availability: st.availability
            })),
            subtitles: order.onPremAssets.subtitles.map((sub) => ({
                assetId: sub.assetId,
                filePath: sub.filePath,
                subtitleType: sub.subtitleType,
                language: sub.language,
                availability: sub.availability,
                targetFilename: sub.targetFilename
            }))
        },
        pONumber: order.pONumber,
        status: mapToOnPremOrderStatus(order.status),
        source: order.source,
        modifiedBy: order.modifiedBy,
        materialSpecification: order.materialSpecification,
        deliveryDetails: order.deliveryDetails,
        deliveryTo: order.deliveryTo,
        isTrailer: order.isTrailer,
        notes: order.notes,
        assetNotes: []
    };
};
